import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../../components-new/SEO/SEO';
import { Hero } from '../../components-new/Hero/Hero';
import { AnimatedSection } from '../../components-new/AnimatedSection/AnimatedSection';
import { ServiceCard } from '../../components-new/ServiceCard/ServiceCard';
import { ServiceModal } from '../../components-new/ServiceModal/ServiceModal';
import { ProjectMarquee } from '../../components-new/ProjectMarquee/ProjectMarquee';
import { packages } from '../../data/services';
import { projects } from '../../data/projects';
import './style.less';

export const Home = () => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);

  const featuredPackages = packages;

  // Prepare projects data for the marquee
  const marqueeProjects = projects.map((project) => ({
    id: project.id,
    image: project.mainImage,
    title: t(`projects.${project.translationKey}.title`),
    style: t(`projects.${project.translationKey}.style`),
  }));

  const handleServiceClick = (service, translationKey) => {
    const serviceData = {
      id: service.id,
      icon: service.icon,
      title: t(`services.${translationKey}.title`),
      price: t(`services.${translationKey}.price`),
      duration:
        t(`services.${translationKey}.duration`) !== `services.${translationKey}.duration`
          ? t(`services.${translationKey}.duration`)
          : null,
      description: t(`services.${translationKey}.description`),
    };

    // Add optional fields if they exist
    const intro = t(`services.${translationKey}.intro`, { defaultValue: '' });
    if (intro) serviceData.intro = intro;

    const priceNote = t(`services.${translationKey}.priceNote`, { defaultValue: '' });
    if (priceNote) serviceData.priceNote = priceNote;

    // Handle includes array
    const includesKey = `services.${translationKey}.includes`;
    const includes = t(includesKey, { returnObjects: true, defaultValue: null });
    if (includes && Array.isArray(includes)) {
      serviceData.includes = includes;
    }

    // Handle price details
    const priceDetailsBase = t(`services.${translationKey}.priceDetails.base`, {
      defaultValue: '',
    });
    if (priceDetailsBase) {
      serviceData.priceDetails = {
        base: priceDetailsBase,
        additional: t(`services.${translationKey}.priceDetails.additional`, {
          returnObjects: true,
          defaultValue: [],
        }),
        note: t(`services.${translationKey}.priceDetails.note`, { defaultValue: '' }),
      };
    }

    setSelectedService(serviceData);
  };

  return (
    <div className="home-page">
      <SEO
        title={t('home.seo.title')}
        description={t('home.seo.description')}
        keywords={t('home.seo.keywords')}
        ogType="website"
      />
      <Hero />

      {/* About Preview Section - Asymmetric Layout */}
      <section className="section about-preview">
        <div className="container">
          <div className="about-preview-content">
            <AnimatedSection className="about-image-wrapper">
              <div className="about-image-container">
                <div className="decorative-frame" aria-hidden="true"></div>
                <img
                  src="/images/about-me/office-desk.jpg"
                  alt={t(
                    'home.aboutPreview.imageAlt',
                    'Модерна всекидневна с елегантен дизайн от SG Interior Design'
                  )}
                  loading="lazy"
                />
                <div className="decorative-accent" aria-hidden="true"></div>
              </div>
            </AnimatedSection>

            <div className="about-text-wrapper">
              <AnimatedSection delay={0.1}>
                <span className="section-label">{t('home.aboutPreview.label')}</span>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <h2>{t('home.aboutPreview.title')}</h2>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <p className="large-text">{t('home.aboutPreview.text')}</p>
              </AnimatedSection>
              <AnimatedSection delay={0.4}>
                <Link to="/about" className="btn-primary">
                  {t('home.aboutPreview.cta')} <i className="fas fa-arrow-right"></i>
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section services-preview">
        <div className="container">
          <AnimatedSection className="section-header text-center">
            <span className="section-label">{t('home.servicesPreview.label')}</span>
            <h2>{t('home.servicesPreview.title')}</h2>
            <p>{t('home.servicesPreview.subtitle')}</p>
          </AnimatedSection>

          <div className="services-grid">
            {featuredPackages.map((pkg, index) => (
              <ServiceCard
                key={pkg.id}
                serviceId={pkg.id}
                icon={pkg.icon}
                title={t(`services.${pkg.translationKey}.title`)}
                price={t(`services.${pkg.translationKey}.price`)}
                description={t(`services.${pkg.translationKey}.shortDescription`)}
                delay={index * 0.1}
                onClick={() => handleServiceClick(pkg, pkg.translationKey)}
                isPackage={true}
              />
            ))}
          </div>

          <AnimatedSection className="text-center" delay={0.4}>
            <Link to="/services" className="btn-secondary">
              {t('home.servicesPreview.cta')} <i className="fas fa-arrow-right"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Projects Section */}
      <section className="section projects-preview">
        <div className="container">
          <AnimatedSection className="section-header text-center">
            <span className="section-label">{t('home.projectsPreview.label')}</span>
            <h2>{t('home.projectsPreview.title')}</h2>
            <p>{t('home.projectsPreview.subtitle')}</p>
          </AnimatedSection>
        </div>

        <ProjectMarquee projects={marqueeProjects} />

        <div className="container">
          <AnimatedSection className="text-center projects-cta" delay={0.4}>
            <Link to="/projects" className="btn-secondary">
              {t('home.projectsPreview.cta')} <i className="fas fa-arrow-right"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonial Section */}
      <section
        className="section testimonial-section"
        aria-label={t('home.testimonial.label', 'Отзиви')}
      >
        <div className="container">
          <AnimatedSection className="testimonial-content">
            <div className="quote-icon" aria-hidden="true">
              <i className="fas fa-quote-left" aria-hidden="true"></i>
            </div>
            <blockquote className="testimonial-quote">
              <p>{t('home.testimonial.quote')}</p>
              <footer className="testimonial-author">
                <cite>{t('home.testimonial.author')}</cite>
              </footer>
            </blockquote>
          </AnimatedSection>
        </div>
      </section>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
      )}
    </div>
  );
};
